import React from 'react';
import { AUTH_PREFIX_PATH, APP_PREFIX_PATH } from 'configs/AppConfig';

export const redirectRoutes = [
  {
    from: '/bookings',
    to: '/bookings/updates',
  },
  {
    from: '/cms',
    to: '/cms/docs',
  },
  {
    from: '/admin',
    to: '/admin/ops',
  },
  {
    from: `${APP_PREFIX_PATH}/dashboards/sales/sotravel`,
    to: `/bookings/updates`,
  },
];

export const noAuthRoutes = [
  {
    key: 'custom-itinerary-public',
    path: '/custom-itinerary/:token',
    component: React.lazy(() =>
      import('_v2/pages/custom-itineraries').then((m) => ({
        default: m.PublicCustomItinerary,
      }))
    ),
  },
  {
    key: 'v2-docs-public',
    path: `/docs/:id`,
    component: React.lazy(() =>
      import('_v2/pages/cms/preview').then((module) => ({
        default: (props) => <module.default {...props} publicPreview />,
      }))
    ),
    withoutLayout: true,
  },
  {
    key: 'trip-public-bookings-page',
    path: `/bookings/trip/:secure_id/:participant?`,
    component: React.lazy(
      () => import('_v2/pages/bookings/history/bookings-public')
    ),
  },
];

export const publicRoutes = [
  {
    key: 'login',
    path: `${AUTH_PREFIX_PATH}/login`,
    component: React.lazy(() => import('_v2/pages/auth/login')),
  },
  {
    key: 'error-page-1',
    path: `${AUTH_PREFIX_PATH}/error-page-1`,
    component: React.lazy(() => import('views/auth-views/errors/error-page-1')),
  },
  {
    key: 'error-page-2',
    path: `${AUTH_PREFIX_PATH}/error-page-2`,
    component: React.lazy(() => import('views/auth-views/errors/error-page-2')),
  },
];

export const protectedRoutes = [
  {
    key: 'v2-custom-itineraries',
    path: '/custom-itineraries',
    component: React.lazy(() => import('_v2/pages/custom-itineraries')),
  },
  {
    key: 'v2-custom-itinerary-edit',
    path: '/custom-itineraries/:id',
    component: React.lazy(() =>
      import('_v2/pages/custom-itineraries').then((m) => ({
        default: m.CustomItineraryEditor,
      }))
    ),
  },
  {
    key: 'vTrips-trips-detail',
    path: `/trips/:itineraryId`,
    component: React.lazy(() => import('_v2/pages/trips/trips-details')),
  },
  {
    key: 'v2-trips',
    path: `/trips`,
    component: React.lazy(() => import('_v2/pages/trips/trips')),
  },
  {
    key: 'v2-manage-trips',
    path: `/trips/:projectId/manage`,
    component: React.lazy(() => import('_v2/pages/trips/manage-trips')),
  },
  // Add-a-trip-date page, two entries (same component). Static "new-date"
  // out-ranks the :itineraryId / :tripId patterns under React Router v6, so
  // order here doesn't matter.
  {
    key: 'v2-create-trip-date',
    path: `/trips/new-date`,
    component: React.lazy(() => import('_v2/pages/trips/create-trip-date')),
  },
  {
    key: 'v2-manage-create-trip-date',
    path: `/trips/:projectId/manage/new-date`,
    component: React.lazy(() => import('_v2/pages/trips/create-trip-date')),
  },
  {
    key: 'v2-manage-trip-detail',
    path: `/trips/:projectId/manage/:tripId`,
    component: React.lazy(() => import('_v2/pages/trips/manage-trip-detail')),
  },
  // Admin-only ops queues (Appsmith Fulfilment migration). The page itself
  // also guards on is_admin.
  {
    key: 'v2-admin-ops',
    path: `/admin/ops`,
    component: React.lazy(() => import('_v2/pages/admin/ops-approvals')),
  },
  {
    key: 'v2-admin-users',
    path: `/admin/users`,
    component: React.lazy(() => import('_v2/pages/admin/user-management')),
  },
  // Admin-only monthly trips fulfilment view (Appsmith Fulfilment landing
  // table migration). Also guards on is_admin. Not /admin/trips — the header's
  // isActive() does substring matching, so that would light up "Itineraries".
  {
    key: 'v2-admin-fulfilment',
    path: `/admin/fulfilment`,
    component: React.lazy(() => import('_v2/pages/admin/trips-overview')),
  },
  // Admin-only monthly trip finance view (Appsmith Data & Insights → Growth
  // Metrics migration). Also guards on is_admin.
  {
    key: 'v2-admin-finance',
    path: `/admin/finance`,
    component: React.lazy(() => import('_v2/pages/admin/finance-overview')),
  },
  // Admin-only marketing tools (Appsmith Marketing-tab migration): homepage
  // posters, notification sender, automations. Guards on is_admin too.
  {
    key: 'v2-admin-marketing',
    path: `/admin/marketing`,
    component: React.lazy(() => import('_v2/pages/admin/marketing')),
  },
  // Admin-only weekly deals curation (welcome offers / last-minute deals).
  // Campaign history + receipts for one marketing campaign.
  {
    key: 'v2-admin-marketing-campaign',
    path: `/admin/marketing/campaigns/:campaignId`,
    component: React.lazy(
      () => import('_v2/pages/admin/marketing/campaigns/CampaignDetail')
    ),
  },
  // Config + run history for one notification automation.
  {
    key: 'v2-admin-marketing-automation',
    path: `/admin/marketing/automations/:automationId`,
    component: React.lazy(
      () => import('_v2/pages/admin/marketing/automations/AutomationDetail')
    ),
  },
  {
    key: 'v2-admin-weekly-deals',
    path: `/admin/weekly-deals`,
    component: React.lazy(() => import('_v2/pages/admin/weekly-deals')),
  },
  // Admin-only guides CMS (SEO landing pages served by the Next.js guides
  // app at sotravel.com/guides/<slug>). Guards on is_admin too.
  {
    key: 'v2-admin-guides',
    path: `/admin/guides`,
    component: React.lazy(() => import('_v2/pages/admin/guides')),
  },
  // Sotravel Club platform: payout queue (clubs + Popular-duck affiliates,
  // migrated from Appsmith) and club administration. Both guard on is_admin.
  {
    key: 'v2-admin-payouts',
    path: `/admin/payouts`,
    component: React.lazy(() => import('_v2/pages/admin/payouts')),
  },
  {
    key: 'v2-admin-clubs',
    path: `/admin/clubs`,
    component: React.lazy(() => import('_v2/pages/admin/clubs')),
  },
  // Trip album bulk upload (team photographers) with automatic people
  // grouping. Two entries, same component: the standalone project → trip
  // picker and one trip's album (linked from the bookings page). Guards on
  // is_admin too.
  {
    key: 'v2-admin-trip-album',
    path: `/admin/trip-album`,
    component: React.lazy(() => import('_v2/pages/admin/trip-album')),
  },
  {
    key: 'v2-admin-trip-album-trip',
    path: `/admin/trip-album/:tripId`,
    component: React.lazy(() => import('_v2/pages/admin/trip-album')),
  },
  {
    key: 'v2-admin-website-configurations',
    path: `/admin/website_configurations`,
    component: React.lazy(
      () => import('_v2/pages/admin/website-configurations')
    ),
  },
  {
    key: 'v2-admin-guide-editor',
    path: `/admin/guides/:guideId`,
    component: React.lazy(() => import('_v2/pages/admin/guides/editor')),
  },
  {
    key: 'v2-docs-template-type',
    path: `/docs-template/:type`,
    component: React.lazy(() =>
      import('_v2/pages/cms/create-docs').then((module) => ({
        default: (props) => (
          <module.default {...props} source="admin-templates" />
        ),
      }))
    ),
  },
  {
    key: 'v2-docs-template',
    path: `/docs-template`,
    component: React.lazy(() => import('_v2/pages/cms/docs-template')),
  },
  {
    key: 'v2-cms.preview',
    path: `/cms/preview`,
    component: React.lazy(() => import('_v2/pages/cms/preview')),
    withoutLayout: true,
  },
  {
    key: 'v2-cms.create-docs',
    path: `/cms/docs/:id`,
    component: React.lazy(() => import('_v2/pages/cms/create-docs')),
  },
  {
    key: 'v2-cms.list',
    path: `/cms/docs`,
    component: React.lazy(() => import('_v2/pages/cms/list')),
  },

  {
    key: 'v2-listings',
    path: `/listings`,
    component: React.lazy(() => import('_v2/pages/coming-soon')),
  },
  {
    key: 'v2-messages',
    path: `/messages`,
    component: React.lazy(() => import('_v2/pages/coming-soon')),
  },
  {
    key: 'v2-insights',
    path: `/insights`,
    component: React.lazy(() => import('_v2/pages/coming-soon')),
  },

  {
    key: 'v2-dashboard.bookings.upcoming-details',
    path: `/bookings/past/:id`,
    component: React.lazy(
      () => import('_v2/pages/bookings/history/bookings-past')
    ),
  },
  {
    key: 'v2-dashboard.bookings.past',
    path: `/bookings/past`,
    component: React.lazy(
      () => import('_v2/pages/bookings/history/bookings-past')
    ),
  },
  {
    key: 'v2-dashboard.bookings.upcoming-details',
    path: `/bookings/upcoming/:id`,
    component: React.lazy(
      () => import('_v2/pages/bookings/history/bookings-upcoming')
    ),
  },
  {
    key: 'v2-dashboard.bookings.upcoming',
    path: `/bookings/upcoming`,
    component: React.lazy(
      () => import('_v2/pages/bookings/history/bookings-upcoming')
    ),
  },
  {
    key: 'v2-dashboard.bookings.updates',
    path: `/bookings/updates`,
    component: React.lazy(() => import('_v2/pages/bookings/bookings-pending')),
  },
  {
    key: 'v2-dashboard.bookings.admin-notifications',
    path: `/bookings/admin-notifications`,
    component: React.lazy(() =>
      import('_v2/pages/bookings/admin-notifications')
    ),
  },
  {
    key: 'v2-dashboard.merchant-payment.list',
    path: `/merchant-payment`,
    component: React.lazy(() => import('_v2/pages/merchant-payment/index')),
  },
  {
    key: 'v2-dashboard.merchant-payment.create',
    path: `/merchant-payment/create`,
    component: React.lazy(() => import('_v2/pages/merchant-payment/create')),
  },
  {
    key: 'v2-dashboard.merchant-payment.edit',
    path: `/merchant-payment/create/:id`,
    component: React.lazy(() => import('_v2/pages/merchant-payment/create')),
  },
  {
    key: 'v2-dashboard.merchant-payment.detail',
    path: `/merchant-payment/:id`,
    component: React.lazy(() => import('_v2/pages/merchant-payment/detail')),
  },
  {
    key: 'v2-dashboard.settings.account',
    path: `/settings/account`,
    component: React.lazy(() => import('_v2/pages/settings/setting/account')),
  },
  {
    key: 'v2-dashboard.settings',
    path: `/settings/tour-assignment`,
    component: React.lazy(
      () => import('_v2/pages/settings/setting/tour-assignment')
    ),
  },
  {
    key: 'v2-dashboard.settings.team-management',
    path: `/settings/team-management`,
    component: React.lazy(
      () => import('_v2/pages/settings/setting/team-management')
    ),
  },
  {
    key: 'internals.trips_management',
    path: `${APP_PREFIX_PATH}/internals/trips_management`,
    component: React.lazy(
      () => import('views/app-views/internals/trips_management')
    ),
  },
  {
    key: 'internals.finance_management',
    path: `${APP_PREFIX_PATH}/internals/finance_management`,
    component: React.lazy(
      () => import('views/app-views/internals/finance_management')
    ),
  },
  {
    key: 'dashboard.default',
    path: `${APP_PREFIX_PATH}/dashboards/default`,
    component: React.lazy(() => import('views/app-views/dashboards/default')),
  },
  {
    key: 'dashboard.analytic',
    path: `${APP_PREFIX_PATH}/dashboards/analytic`,
    component: React.lazy(() => import('views/app-views/dashboards/analytic')),
  },
  {
    key: 'dashboard.sales',
    path: `${APP_PREFIX_PATH}/dashboards/sales`,
    component: React.lazy(() => import('views/app-views/dashboards/sales')),
  },
  {
    key: 'apps',
    path: `${APP_PREFIX_PATH}/apps`,
    component: React.lazy(() => import('views/app-views/apps')),
  },
  {
    key: 'apps.mail',
    path: `${APP_PREFIX_PATH}/apps/mail/*`,
    component: React.lazy(() => import('views/app-views/apps/mail')),
  },
  {
    key: 'apps.chat',
    path: `${APP_PREFIX_PATH}/apps/chat/*`,
    component: React.lazy(() => import('views/app-views/apps/chat')),
  },
  {
    key: 'apps.calendar',
    path: `${APP_PREFIX_PATH}/apps/calendar`,
    component: React.lazy(() => import('views/app-views/apps/calendar')),
  },
  {
    key: 'apps.project',
    path: `${APP_PREFIX_PATH}/apps/project`,
    component: React.lazy(() => import('views/app-views/apps/project')),
  },
  {
    key: 'apps.project.list',
    path: `${APP_PREFIX_PATH}/apps/project/list`,
    component: React.lazy(
      () => import('views/app-views/apps/project/project-list/ProjectList')
    ),
  },
  {
    key: 'apps.project.scrumboard',
    path: `${APP_PREFIX_PATH}/apps/project/scrumboard`,
    component: React.lazy(
      () => import('views/app-views/apps/project/scrumboard')
    ),
  },
  {
    key: 'apps.ecommerce',
    path: `${APP_PREFIX_PATH}/apps/ecommerce`,
    component: React.lazy(() => import('views/app-views/apps/e-commerce')),
  },
  {
    key: 'apps.ecommerce.add-product',
    path: `${APP_PREFIX_PATH}/apps/ecommerce/add-product`,
    component: React.lazy(
      () => import('views/app-views/apps/e-commerce/add-product')
    ),
  },
  {
    key: 'apps.ecommerce.edit-product',
    path: `${APP_PREFIX_PATH}/apps/ecommerce/edit-product/:id`,
    component: React.lazy(
      () => import('views/app-views/apps/e-commerce/edit-product')
    ),
  },
  {
    key: 'apps.ecommerce.product-list',
    path: `${APP_PREFIX_PATH}/apps/ecommerce/product-list`,
    component: React.lazy(
      () => import('views/app-views/apps/e-commerce/product-list')
    ),
  },
  {
    key: 'apps.ecommerce.orders',
    path: `${APP_PREFIX_PATH}/apps/ecommerce/orders`,
    component: React.lazy(
      () => import('views/app-views/apps/e-commerce/orders')
    ),
  },
  {
    key: 'components.general',
    path: `${APP_PREFIX_PATH}/components/general`,
    component: React.lazy(() => import('views/app-views/components/general')),
  },
  {
    key: 'components.general.button',
    path: `${APP_PREFIX_PATH}/components/general/button`,
    component: React.lazy(
      () => import('views/app-views/components/general/button')
    ),
  },
  {
    key: 'components.general.icon',
    path: `${APP_PREFIX_PATH}/components/general/icon`,
    component: React.lazy(
      () => import('views/app-views/components/general/icon')
    ),
  },
  {
    key: 'components.general.typography',
    path: `${APP_PREFIX_PATH}/components/general/typography`,
    component: React.lazy(
      () => import('views/app-views/components/general/typography')
    ),
  },
  {
    key: 'components.general',
    path: `${APP_PREFIX_PATH}/components/layout`,
    component: React.lazy(() => import('views/app-views/components/layout')),
  },
  {
    key: 'components.general.grid',
    path: `${APP_PREFIX_PATH}/components/layout/grid`,
    component: React.lazy(
      () => import('views/app-views/components/layout/grid')
    ),
  },
  {
    key: 'components.general.layout',
    path: `${APP_PREFIX_PATH}/components/layout/layout`,
    component: React.lazy(
      () => import('views/app-views/components/layout/layout')
    ),
  },
  {
    key: 'components.navigation',
    path: `${APP_PREFIX_PATH}/components/navigation`,
    component: React.lazy(
      () => import('views/app-views/components/navigation')
    ),
  },
  {
    key: 'components.navigation.affix',
    path: `${APP_PREFIX_PATH}/components/navigation/affix`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/affix')
    ),
  },
  {
    key: 'components.navigation.breadcrumb',
    path: `${APP_PREFIX_PATH}/components/navigation/breadcrumb`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/breadcrumb')
    ),
  },
  {
    key: 'components.navigation.dropdown',
    path: `${APP_PREFIX_PATH}/components/navigation/dropdown`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/dropdown')
    ),
  },
  {
    key: 'components.navigation.menu',
    path: `${APP_PREFIX_PATH}/components/navigation/menu`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/menu')
    ),
  },
  {
    key: 'components.navigation.pagination',
    path: `${APP_PREFIX_PATH}/components/navigation/pagination`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/pagination')
    ),
  },
  {
    key: 'components.navigation.steps',
    path: `${APP_PREFIX_PATH}/components/navigation/steps`,
    component: React.lazy(
      () => import('views/app-views/components/navigation/steps')
    ),
  },
  {
    key: 'components.data-entry',
    path: `${APP_PREFIX_PATH}/components/data-entry`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry')
    ),
  },
  {
    key: 'components.data-entry.auto-complete',
    path: `${APP_PREFIX_PATH}/components/data-entry/auto-complete`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/auto-complete')
    ),
  },
  {
    key: 'components.data-entry.cascader',
    path: `${APP_PREFIX_PATH}/components/data-entry/cascader`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/cascader')
    ),
  },
  {
    key: 'components.data-entry.checkbox',
    path: `${APP_PREFIX_PATH}/components/data-entry/checkbox`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/checkbox')
    ),
  },
  {
    key: 'components.data-entry.date-picker',
    path: `${APP_PREFIX_PATH}/components/data-entry/date-picker`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/date-picker')
    ),
  },
  {
    key: 'components.data-entry.form',
    path: `${APP_PREFIX_PATH}/components/data-entry/form`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/form')
    ),
  },
  {
    key: 'components.data-entry.input',
    path: `${APP_PREFIX_PATH}/components/data-entry/input`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/input')
    ),
  },
  {
    key: 'components.data-entry.input-number',
    path: `${APP_PREFIX_PATH}/components/data-entry/input-number`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/input-number')
    ),
  },
  {
    key: 'components.data-entry.mentions',
    path: `${APP_PREFIX_PATH}/components/data-entry/mentions`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/mentions')
    ),
  },
  {
    key: 'components.data-entry.radio',
    path: `${APP_PREFIX_PATH}/components/data-entry/radio`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/radio')
    ),
  },
  {
    key: 'components.data-entry.rate',
    path: `${APP_PREFIX_PATH}/components/data-entry/rate`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/rate')
    ),
  },
  {
    key: 'components.data-entry.select',
    path: `${APP_PREFIX_PATH}/components/data-entry/select`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/select')
    ),
  },
  {
    key: 'components.data-entry.slider',
    path: `${APP_PREFIX_PATH}/components/data-entry/slider`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/slider')
    ),
  },
  {
    key: 'components.data-entry.switch',
    path: `${APP_PREFIX_PATH}/components/data-entry/switch`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/switch')
    ),
  },
  {
    key: 'components.data-entry.time-picker',
    path: `${APP_PREFIX_PATH}/components/data-entry/time-picker`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/time-picker')
    ),
  },
  {
    key: 'components.data-entry.transfer',
    path: `${APP_PREFIX_PATH}/components/data-entry/transfer`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/transfer')
    ),
  },
  {
    key: 'components.data-entry.tree-select',
    path: `${APP_PREFIX_PATH}/components/data-entry/tree-select`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/tree-select')
    ),
  },
  {
    key: 'components.data-entry.upload',
    path: `${APP_PREFIX_PATH}/components/data-entry/upload`,
    component: React.lazy(
      () => import('views/app-views/components/data-entry/upload')
    ),
  },
  {
    key: 'components.data-display',
    path: `${APP_PREFIX_PATH}/components/data-display`,
    component: React.lazy(
      () => import('views/app-views/components/data-display')
    ),
  },
  {
    key: 'components.data-display.avatar',
    path: `${APP_PREFIX_PATH}/components/data-display/avatar`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/avatar')
    ),
  },
  {
    key: 'components.data-display.badge',
    path: `${APP_PREFIX_PATH}/components/data-display/badge`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/badge')
    ),
  },
  {
    key: 'components.data-display.calendar',
    path: `${APP_PREFIX_PATH}/components/data-display/calendar`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/calendar')
    ),
  },
  {
    key: 'components.data-display.card',
    path: `${APP_PREFIX_PATH}/components/data-display/card`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/card')
    ),
  },
  {
    key: 'components.data-display.carousel',
    path: `${APP_PREFIX_PATH}/components/data-display/carousel`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/carousel')
    ),
  },
  {
    key: 'components.data-display.collapse',
    path: `${APP_PREFIX_PATH}/components/data-display/collapse`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/collapse')
    ),
  },
  {
    key: 'components.data-display.comment',
    path: `${APP_PREFIX_PATH}/components/data-display/comment`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/comment')
    ),
  },
  {
    key: 'components.data-display.descriptions',
    path: `${APP_PREFIX_PATH}/components/data-display/descriptions`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/descriptions')
    ),
  },
  {
    key: 'components.data-display.empty',
    path: `${APP_PREFIX_PATH}/components/data-display/empty`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/empty')
    ),
  },
  {
    key: 'components.data-display.list',
    path: `${APP_PREFIX_PATH}/components/data-display/list`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/list')
    ),
  },
  {
    key: 'components.data-display.popover',
    path: `${APP_PREFIX_PATH}/components/data-display/popover`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/popover')
    ),
  },
  {
    key: 'components.data-display.statistic',
    path: `${APP_PREFIX_PATH}/components/data-display/statistic`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/statistic')
    ),
  },
  {
    key: 'components.data-display.table',
    path: `${APP_PREFIX_PATH}/components/data-display/table`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/table')
    ),
  },
  {
    key: 'components.data-display.tabs',
    path: `${APP_PREFIX_PATH}/components/data-display/tabs`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/tabs')
    ),
  },
  {
    key: 'components.data-display.tag',
    path: `${APP_PREFIX_PATH}/components/data-display/tag`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/tag')
    ),
  },
  {
    key: 'components.data-display.timeline',
    path: `${APP_PREFIX_PATH}/components/data-display/timeline`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/timeline')
    ),
  },
  {
    key: 'components.data-display.tooltip',
    path: `${APP_PREFIX_PATH}/components/data-display/tooltip`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/tooltip')
    ),
  },
  {
    key: 'components.data-display.tree',
    path: `${APP_PREFIX_PATH}/components/data-display/tree`,
    component: React.lazy(
      () => import('views/app-views/components/data-display/tree')
    ),
  },
  {
    key: 'components.feedback',
    path: `${APP_PREFIX_PATH}/components/feedback`,
    component: React.lazy(() => import('views/app-views/components/feedback')),
  },
  {
    key: 'components.feedback.alert',
    path: `${APP_PREFIX_PATH}/components/feedback/alert`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/alert')
    ),
  },
  {
    key: 'components.feedback.drawer',
    path: `${APP_PREFIX_PATH}/components/feedback/drawer`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/drawer')
    ),
  },
  {
    key: 'components.feedback.message',
    path: `${APP_PREFIX_PATH}/components/feedback/message`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/message')
    ),
  },
  {
    key: 'components.feedback.modal',
    path: `${APP_PREFIX_PATH}/components/feedback/modal`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/modal')
    ),
  },
  {
    key: 'components.feedback.notification',
    path: `${APP_PREFIX_PATH}/components/feedback/notification`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/notification')
    ),
  },
  {
    key: 'components.feedback.popconfirm',
    path: `${APP_PREFIX_PATH}/components/feedback/popconfirm`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/popconfirm')
    ),
  },
  {
    key: 'components.feedback.progress',
    path: `${APP_PREFIX_PATH}/components/feedback/progress`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/progress')
    ),
  },
  {
    key: 'components.feedback.result',
    path: `${APP_PREFIX_PATH}/components/feedback/result`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/result')
    ),
  },
  {
    key: 'components.feedback.skeleton',
    path: `${APP_PREFIX_PATH}/components/feedback/skeleton`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/skeleton')
    ),
  },
  {
    key: 'components.feedback.spin',
    path: `${APP_PREFIX_PATH}/components/feedback/spin`,
    component: React.lazy(
      () => import('views/app-views/components/feedback/spin')
    ),
  },
  {
    key: 'components.other',
    path: `${APP_PREFIX_PATH}/components/other`,
    component: React.lazy(() => import('views/app-views/components/other')),
  },
  {
    key: 'components.other',
    path: `${APP_PREFIX_PATH}/components/other`,
    component: React.lazy(() => import('views/app-views/components/other')),
  },
  {
    key: 'components.other',
    path: `${APP_PREFIX_PATH}/components/other`,
    component: React.lazy(() => import('views/app-views/components/other')),
  },
  {
    key: 'components.other.anchor',
    path: `${APP_PREFIX_PATH}/components/other/anchor`,
    component: React.lazy(
      () => import('views/app-views/components/other/anchor')
    ),
  },
  {
    key: 'components.other.backtop',
    path: `${APP_PREFIX_PATH}/components/other/backtop`,
    component: React.lazy(
      () => import('views/app-views/components/other/backtop')
    ),
  },
  {
    key: 'components.other.config-provider',
    path: `${APP_PREFIX_PATH}/components/other/config-provider`,
    component: React.lazy(
      () => import('views/app-views/components/other/config-provider')
    ),
  },
  {
    key: 'components.other.divider',
    path: `${APP_PREFIX_PATH}/components/other/divider`,
    component: React.lazy(
      () => import('views/app-views/components/other/divider')
    ),
  },
  {
    key: 'components.chart',
    path: `${APP_PREFIX_PATH}/components/charts`,
    component: React.lazy(() => import('views/app-views/components/charts')),
  },
  {
    key: 'components.chart.apex-charts',
    path: `${APP_PREFIX_PATH}/components/charts/apex-charts`,
    component: React.lazy(
      () => import('views/app-views/components/charts/apex')
    ),
  },
  {
    key: 'components.chart.chartjs',
    path: `${APP_PREFIX_PATH}/components/charts/chartjs`,
    component: React.lazy(
      () => import('views/app-views/components/charts/chartjs')
    ),
  },
  {
    key: 'components.maps',
    path: `${APP_PREFIX_PATH}/components/maps`,
    component: React.lazy(() => import('views/app-views/components/maps')),
  },
  {
    key: 'components.maps.simple-map',
    path: `${APP_PREFIX_PATH}/components/maps/simple-map`,
    component: React.lazy(
      () => import('views/app-views/components/maps/simple-map')
    ),
  },
  {
    key: 'error-page-1',
    path: `${APP_PREFIX_PATH}/error-page-1`,
    component: React.lazy(() => import('views/auth-views/errors/error-page-1')),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: 'error-page-2',
    path: `${APP_PREFIX_PATH}/error-page-2`,
    component: React.lazy(() => import('views/auth-views/errors/error-page-2')),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: 'pages',
    path: `${APP_PREFIX_PATH}/pages`,
    component: React.lazy(() => import('views/app-views/pages')),
  },
  {
    key: 'pages.profile',
    path: `${APP_PREFIX_PATH}/pages/profile`,
    component: React.lazy(() => import('views/app-views/pages/profile')),
  },
  {
    key: 'pages.invoice',
    path: `${APP_PREFIX_PATH}/pages/invoice`,
    component: React.lazy(() => import('views/app-views/pages/invoice')),
  },
  {
    key: 'pages.pricing',
    path: `${APP_PREFIX_PATH}/pages/pricing`,
    component: React.lazy(() => import('views/app-views/pages/pricing')),
  },
  {
    key: 'pages.faq',
    path: `${APP_PREFIX_PATH}/pages/faq`,
    component: React.lazy(() => import('views/app-views/pages/faq')),
  },
  {
    key: 'pages.setting',
    path: `${APP_PREFIX_PATH}/pages/setting/*`,
    component: React.lazy(() => import('views/app-views/pages/setting')),
  },
  {
    key: 'pages.user-list',
    path: `${APP_PREFIX_PATH}/pages/user-list`,
    component: React.lazy(() => import('views/app-views/pages/user-list')),
  },
  {
    key: 'docs.documentation',
    path: `${APP_PREFIX_PATH}/docs/documentation/*`,
    component: React.lazy(() => import('views/app-views/docs')),
  },
];
